import { Hemisphere } from 'shared/enums';
import { AreaUnit, DistanceUnit, fillArray } from 'src/shared/helpers';
import {
  CoordinateFormatType,
  LineMeasurementUnit,
  PolygonMeasurementUnit,
} from './enums';
import type {
  CoordinateFormats,
  FeatureInfoImageObjectType,
  FeatureInfoInputType,
  FeaturePropertiesType,
  MeasurementOptionsType,
} from './types';

export const lineMeasurementUnitAbbreviations: Record<
  LineMeasurementUnit,
  string
> = {
  [LineMeasurementUnit.Millimeters]: 'mm',
  [LineMeasurementUnit.Centimeters]: 'cm',
  [LineMeasurementUnit.Meters]: 'm',
  [LineMeasurementUnit.Kilometers]: 'km',
  [LineMeasurementUnit.Inches]: 'in',
  [LineMeasurementUnit.Feet]: 'ft',
  [LineMeasurementUnit.Yards]: 'yd',
  [LineMeasurementUnit.Miles]: 'mi',
};

export const polygonUnitAbbreviations: Record<PolygonMeasurementUnit, string> =
  {
    [PolygonMeasurementUnit.SquareCentimeters]: 'cm²',
    [PolygonMeasurementUnit.SquareMeters]: 'm²',
    [PolygonMeasurementUnit.SquareKilometers]: 'km²',
    [PolygonMeasurementUnit.Hectares]: 'ha',
    [PolygonMeasurementUnit.Acres]: 'ac',
    [PolygonMeasurementUnit.SquareFeet]: 'ft²',
    [PolygonMeasurementUnit.SquareYards]: 'yd²',
    [PolygonMeasurementUnit.SquareMiles]: 'mi²',
  };

export const areaToDistanceUnit: Record<AreaUnit, DistanceUnit> = {
  [AreaUnit.SquareCentimeter]: DistanceUnit.Centimeter,
  [AreaUnit.SquareMeter]: DistanceUnit.Meter,
  [AreaUnit.SquareKilometer]: DistanceUnit.Kilometer,
  [AreaUnit.Hectare]: DistanceUnit.Meter,
  [AreaUnit.Acre]: DistanceUnit.Foot,
  [AreaUnit.SquareFoot]: DistanceUnit.Foot,
  [AreaUnit.SquareYard]: DistanceUnit.Yard,
  [AreaUnit.SquareMile]: DistanceUnit.Mile,
};

export const coordinateFormatOptions: MeasurementOptionsType[] = [
  {
    label: CoordinateFormatType.UTM,
    value: CoordinateFormatType.UTM,
    customAbbreviation: 'UTM',
  },
  {
    label: CoordinateFormatType.DecimalDegrees,
    value: CoordinateFormatType.DecimalDegrees,
    customAbbreviation: 'DD',
  },
  {
    label: CoordinateFormatType.DegreesMinutesSeconds,
    value: CoordinateFormatType.DegreesMinutesSeconds,
    customAbbreviation: 'DMS',
  },
];

export const lineMeasurementOptions: MeasurementOptionsType[] = Object.entries(
  lineMeasurementUnitAbbreviations,
).map(([unit, abbreviation]) => ({
  label: unit,
  value: unit,
  customAbbreviation: abbreviation,
}));

export const polygonMeasurementOptions: MeasurementOptionsType[] =
  Object.entries(polygonUnitAbbreviations).map(([label, unit]) => ({
    label,
    value: label,
    customAbbreviation: unit,
  }));

/**
 * imageSource will either contain the base64String if the image is not uploaded.
 * Or it will contain the imageS3Key, if image is fetched from backend.
 */
export const initialImageState: FeatureInfoImageObjectType = {
  imageFile: null,
  isUploaded: false,
  imageSource: null,
  thumbnailSource: null,
  id: '',
  name: '',
};

export const featureInfoInitialInputState: FeatureInfoInputType = {
  featureId: '',
  featureInformation: '',
  featureImages: fillArray(3, initialImageState),
  featureMeasurementFormat: null,
};

export const initialFeatureProperties: FeaturePropertiesType = {
  planarDistance: null,
  surfaceDistance: null,
  planarArea: null,
  surfaceArea: null,
  planarPerimeter: null,
  surfacePerimeter: null,
};

export const initialCoordinateFormats: CoordinateFormats = {
  zoneNumber: 0,
  hemisphere: Hemisphere.UNKNOWN,
  utmCoords: {
    easting: '',
    northing: '',
  },
  ddLongitude: '',
  ddLatitude: '',
  dmsLongitude: '',
  dmsLatitude: '',
};
